import moment from 'moment';
import PropTypes from 'prop-types';
import React from 'react';

import { Table } from 'react-bootstrap';
import { Marker, Popup } from 'react-leaflet';

import L from 'leaflet';
import { useAppState } from '../../contexts/AppStateProvider';
import { useLanguageState } from '../../contexts/LanguageProvider';

/**
 * Convert magnitude to number
 * @param {number} magnitude 
 */
export const magnitudeToRadiusBinned = (magnitude) => {
  if (magnitude < 2) return 2.5;
  if (magnitude < 3) return 4;
  if (magnitude < 4) return 6;
  if (magnitude < 5) return 8;
  if (magnitude < 6) return 10;
  return 12;
};
/**
 * Convert datetime to color
 * @param {string} datetime 
 */
export const datettimeToColor = (datetime, isLatest) => {
  const curr = moment();
  let hours = moment.duration(curr.diff(moment(datetime))).asHours();
  if (isLatest) {
    return "#cc00ff"; // purple
  }
  else if (hours < 24) {
    return '#e31a1c'; // Red
  }
  else if (hours < 24 * 7) {
    return '#FFA500'; // Orange
  }
  else if (hours < 24 * 30) {
    return '#FFFF00'; // Yellow
  }
  return '#FFFFFF'; // White
};


const event2marker = (magnitude, type, certainty, color) => {
  const radius = magnitudeToRadiusBinned(magnitude);
  const height = radius * 2;
  const windowLength = height + 2;
  // circle needs to be a bit larger because of stroke cut-off
  const shapeCircle = `
  <svg height="${windowLength}" width="${windowLength}">
    <circle cx="${radius + 1}" cy="${radius + 1}" r="${radius}" fill="${color}" stroke="black"/>
  </svg>`;

  // rect is ok for stroke padding
  const shapeSquare = `
  <svg height="${height}" width="${height}">
    <rect width="${height}" height="${height}" fill="${color}" stroke="black"/>
  </svg>`;

  const shapePentagon = `
  <svg height="${windowLength}" width="${windowLength}">
    <polygon
      points="1,${height * 0.4 + 1} ${height * .5 + 1},1 ${height + 1},${height * .4 + 1} ${height * 0.75 + 1},${height + 1} ${height * 0.25 + 1},${height + 1}" fill="${color}" stroke="black"
    /></svg>
  `;
  const shapeDiamond = `
  <svg height="${windowLength}" width="${windowLength}">
    <polygon
      points="${height * 0.5 + 1},1 ${height + 1},${height * .5 + 1} ${height * .5 + 1},${height + 1} 1,${height * 0.5 + 1}" fill="${color}" stroke="black"
    /></svg>
  `;
  const shapeHexagon = `
  <svg height="${windowLength}" width="${windowLength}">
    <polygon
      points="${height * .1 + 1},${height * .3 + 1} ${height * .1 + 1},${height * .7 + 1} ${height * .5 + 1},${height + 1} ${height * 0.9 + 1},${height * .7 + 1} ${height * .9 + 1},${height * .3 + 1} ${height * .5 + 1},1" fill="${color}" stroke="black"
    /></svg>
  `;
  const shapeTriangle = `
  <svg height="${windowLength}" width="${windowLength}">
    <polygon
      points="1,1 ${height + 1},1 ${height * 0.5 + 1},${height + 1}" fill="${color}" stroke="black"
    /></svg>
  `;
  const eventMarkerMap = {
    "earthquake": shapeCircle,
    "quarry blast": shapeSquare,
    "mining explosion": shapeSquare,
    "controlled explosion": shapeSquare,
    "industrial explosion": shapeSquare,
    "road cut": shapeSquare,
    "blasting levee": shapeSquare,
    "induced or triggered event": shapeDiamond,
    "rock burst": shapeDiamond,
    "mine collapse": shapeDiamond,
    "fluid injection": shapeDiamond,
    "fluid extraction": shapeDiamond,
    "reservoir loading": shapeDiamond,
    "industry-related": shapeDiamond,
    "mining-induced": shapeDiamond,

  }

  const marker = (type in eventMarkerMap) ? eventMarkerMap[type] : shapeTriangle;
  // reduce the color internsity
  if (certainty === 'suspected') {
    if (marker === shapeSquare) return shapePentagon;
    if (marker === shapeDiamond) return shapeHexagon;
  }

  return marker;
}



const MarkerEvent = (event, isLatest) => {
  const radius = magnitudeToRadiusBinned(event.magnitude);
  return L.divIcon({
    html: event2marker(event.magnitude, event.eventType, event.typeCertainty, datettimeToColor(event.datetime, isLatest)),
    className: "",
    iconSize: [radius * 2, radius * 2],
    iconAnchor: [radius, radius],
  })
}
/** React component */
const EventsMapMarker = ({
  event,
  isLatest,
  noPopup = false,
  onClick = () => { },
  onMouseOver = () => { },
}) => {
  const [lang] = useLanguageState();
  const latitude = (event.latitude >= 0) ?
    (<span>{event.latitude.toFixed(3)}{lang.map.north}</span>) :
    (<span>{-event.latitude.toFixed(3)}{lang.map.south}</span>);

  const longitude = (event.longitude[0] >= 0) ?
    (<span>{event.longitude.toFixed(3)}{lang.map.east}</span>) :
    (<span>{(-event.longitude).toFixed(3)}{lang.map.west}</span>);

  const magnitude = event.magnitude;
  const [state] = useAppState();

  return (
    <Marker
      key={event.event_id}
      position={[event.latitude, event.longitude]}
      icon={MarkerEvent(event, isLatest)}
      opacity={1.0}
      eventHandlers={{
        click: () => onClick(event.event_id),
        mouseover: () => onMouseOver(event.event_id),
      }}
    >
      {(noPopup) ? null :
        <Popup>
          <Table bordered condensed style={{ fontSize: '12px' }}>
            <tbody>
              <tr><th>{lang.map.eventid}</th><td>{event.eventid}</td></tr>
              <tr><th>{lang.map.eventType}</th><td>{event.getEventTypeAndCertainty(lang.abbr, lang.eventTypeTranslator, lang.eventCertaintyTranslator)}</td></tr>
              <tr><th>{lang.map.datetime}</th><td>{moment.utc(event.datetime).format(state.datetime.format)}</td></tr>
              <tr><th>{lang.map.magnitude}</th><td>{(event.magnitudeType) ? `${magnitude} ${event.magnitudeType}` : magnitude}</td></tr>
              <tr><th>{lang.map.coordinates}</th><td>{latitude}, {longitude}</td></tr>
              <tr><th>{lang.map.depth}</th><td>{event.depth.toFixed(1)}</td></tr>
              <tr><th>{lang.map.description}</th><td>{event.getDescription(lang.abbr)}</td></tr>
            </tbody>
          </Table>
        </Popup>
      }
    </Marker>
  );
}

EventsMapMarker.propTypes = {
  /**
   * Event to display
   */
  event: PropTypes.object.isRequired,
  /**
   * Show it as the latest marker variant
   */
  isLatest: PropTypes.bool,
}

EventsMapMarker.defaultProps = {
  isLatest: false,
}

export default EventsMapMarker;